/**
 * RecentSearches - Manages recent drug search history using localStorage
 *
 * Features:
 * - Stores last 5 unique searches
 * - Displays recent searches as clickable pills
 * - Allows clearing search history
 * - Privacy-focused: data stays in browser only
 *
 * Usage:
 *   RecentSearches.init();  // Initialize on page load
 *   RecentSearches.add({ drug: 'Lipitor', zip: '94107' });  // Add a search
 *   RecentSearches.clear();  // Clear all history
 */

const RecentSearches = {
  STORAGE_KEY: 'arx_recent_searches',
  MAX_ITEMS: 5,
  containerSelector: '#recent-searches-container',
  listSelector: '#recent-searches-list',

  /**
   * Initialize recent searches display
   */
  init() {
    this.render();
    this.bindEvents();
  },

  /**
   * Get all recent searches from localStorage
   * @returns {Array} Array of search objects
   */
  getAll() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error reading recent searches:', e);
      return [];
    }
  },

  /**
   * Add a new search to history
   * @param {Object} search - Search object with drug and optional zip
   */
  add(search) {
    if (!search || !search.drug) return;

    try {
      let searches = this.getAll();

      // Normalize drug name for comparison
      const normalizedDrug = search.drug.trim().toLowerCase();

      // Remove existing entry for same drug (to move to top)
      searches = searches.filter(s =>
        s.drug.trim().toLowerCase() !== normalizedDrug
      );

      // Add new search at the beginning
      searches.unshift({
        drug: search.drug.trim(),
        zip: search.zip || null,
        timestamp: new Date().toISOString()
      });

      // Keep only MAX_ITEMS
      searches = searches.slice(0, this.MAX_ITEMS);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(searches));
      this.render();
    } catch (e) {
      console.error('Error saving recent search:', e);
    }
  },

  /**
   * Clear all recent searches
   */
  clear() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      this.render();
    } catch (e) {
      console.error('Error clearing recent searches:', e);
    }
  },

  /**
   * Remove a specific search by drug name
   * @param {string} drug - Drug name to remove
   */
  remove(drug) {
    try {
      let searches = this.getAll();
      const normalizedDrug = drug.trim().toLowerCase();
      searches = searches.filter(s =>
        s.drug.trim().toLowerCase() !== normalizedDrug
      );
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(searches));
      this.render();
    } catch (e) {
      console.error('Error removing search:', e);
    }
  },

  /**
   * Render recent searches to the DOM
   */
  render() {
    const container = document.querySelector(this.containerSelector);
    const list = document.querySelector(this.listSelector);

    if (!container || !list) return;

    const searches = this.getAll();

    if (searches.length === 0) {
      container.classList.add('hidden');
      return;
    }

    container.classList.remove('hidden');

    // Build HTML for search pills
    const html = searches.map(search => {
      const url = `/drug?q=${encodeURIComponent(search.drug)}${search.zip ? `&zip=${encodeURIComponent(search.zip)}` : ''}`;
      return `
        <a href="${url}"
           class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white ring-1 ring-slate-200 text-sm text-slate-700 hover:bg-violet-50 hover:ring-violet-200 hover:text-violet-700 transition-all duration-150 group"
           title="Search for ${this.escapeHtml(search.drug)}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-400 group-hover:text-violet-500">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span class="font-medium">${this.escapeHtml(search.drug)}</span>
        </a>
      `;
    }).join('');

    list.innerHTML = html;
  },

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Clear button
    const clearBtn = document.querySelector('#clear-recent-searches');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clear();
      });
    }

    // Listen for form submissions to save searches
    const searchForm = document.querySelector('.alt-search');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        const drugInput = searchForm.querySelector('input[name="q"]');
        const zipInput = searchForm.querySelector('input[name="zip"]');

        if (drugInput && drugInput.value.trim()) {
          this.add({
            drug: drugInput.value.trim(),
            zip: zipInput ? zipInput.value.trim() : null
          });
        }
      });
    }
  },

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
};

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  RecentSearches.init();
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecentSearches;
}

// Make available globally
window.RecentSearches = RecentSearches;
